"use client";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatBRL } from "@/helper/currency";

interface Projection {
  mes: string | number;
  valor_gasto_total: number;
  valor_lucro_liquido_sozinho: number;
  valor_lucro_liquido_com_corretor: number;
  roi_solo: number;
  roi_com_corretor: number;
  valor_investimento_selic: number;
  retorno_selic: number;
  [key: string]: any;
}

interface ProfitTableProps {
  data: Projection[];
  className?: string;
}

export const ProfitTable = ({ data, className }: ProfitTableProps) => {
  // Format data to ensure mes is a string format 'Mês X'
  const formattedData = data.map((item) => ({
    ...item,
    mes: typeof item.mes === "number" ? `Mês ${item.mes}` : item.mes,
  }));

  return (
    <>
      <div className="hidden sm:block">
        <div className={`border rounded-lg overflow-hidden ${className || ""}`}>
          {/* Desktop Table - Hidden on mobile, shown on sm and above */}
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-medium text-center">Mês</TableHead>
                <TableHead className="font-medium text-center">
                  Gasto total
                </TableHead>
                <TableHead className="font-medium text-center">
                  Lucro líquido sozinho
                </TableHead>
                <TableHead className="font-medium text-center">
                  Lucro líquido com corretor
                </TableHead>
                <TableHead className="font-medium text-center">
                  ROI sozinho
                </TableHead>
                <TableHead className="font-medium text-center">
                  ROI com corretor
                </TableHead>
                <TableHead className="font-medium text-center">
                  Investindo à SELIC (14,25%)
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {formattedData.map((projection) => (
                <TableRow key={projection.mes}>
                  <TableCell className="font-medium text-center">
                    {projection.mes}
                  </TableCell>
                  <TableCell className="text-center">
                    {formatBRL(projection.valor_gasto_total)}
                  </TableCell>
                  <TableCell
                    className={`text-center ${projection.valor_lucro_liquido_sozinho >= 0 ? "text-emerald-600" : "text-red-600"}`}
                  >
                    {formatBRL(projection.valor_lucro_liquido_sozinho)}
                  </TableCell>
                  <TableCell
                    className={`text-center ${projection.valor_lucro_liquido_com_corretor >= 0 ? "text-emerald-600" : "text-red-600"}`}
                  >
                    {formatBRL(projection.valor_lucro_liquido_com_corretor)}
                  </TableCell>
                  <TableCell
                    className={`text-center ${projection.roi_solo >= 0 ? "text-emerald-600" : "text-red-600"}`}
                  >
                    {projection.roi_solo}%
                  </TableCell>
                  <TableCell
                    className={`text-center ${projection.roi_com_corretor >= 0 ? "text-emerald-600" : "text-red-600"}`}
                  >
                    {projection.roi_com_corretor}%
                  </TableCell>
                  <TableCell className="text-center text-blue-600">
                    {formatBRL(projection.valor_investimento_selic || 0)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Mobile Card View - Shown on mobile, hidden on sm and above */}
      <div className="block sm:hidden space-y-4 p-2">
        {formattedData.map((projection) => (
          <div
            key={projection.mes}
            className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
          >
            <div className="bg-gray-50 py-3 px-4 border-b border-gray-100">
              <p className="text-base font-semibold text-center">
                {projection.mes}
              </p>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex justify-between items-center py-1 border-b border-gray-50">
                <p className="text-sm font-medium text-gray-600">Gasto total</p>
                <p className="text-sm font-semibold">
                  {formatBRL(projection.valor_gasto_total)}
                </p>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-gray-50">
                <p className="text-sm font-medium text-gray-600">
                  Lucro líquido sozinho
                </p>
                <p
                  className={`text-sm font-semibold ${projection.valor_lucro_liquido_sozinho >= 0 ? "text-emerald-600" : "text-red-600"}`}
                >
                  {formatBRL(projection.valor_lucro_liquido_sozinho)}
                </p>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-gray-50">
                <p className="text-sm font-medium text-gray-600">
                  Lucro líquido com corretor
                </p>
                <p
                  className={`text-sm font-semibold ${projection.valor_lucro_liquido_com_corretor >= 0 ? "text-emerald-600" : "text-red-600"}`}
                >
                  {formatBRL(projection.valor_lucro_liquido_com_corretor)}
                </p>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-gray-50">
                <p className="text-sm font-medium text-gray-600">ROI sozinho</p>
                <p
                  className={`text-sm font-semibold ${projection.roi_solo >= 0 ? "text-emerald-600" : "text-red-600"}`}
                >
                  {projection.roi_solo}%
                </p>
              </div>
              <div className="flex justify-between items-center py-1">
                <p className="text-sm font-medium text-gray-600">
                  ROI com corretor
                </p>
                <p
                  className={`text-sm font-semibold ${projection.roi_com_corretor >= 0 ? "text-emerald-600" : "text-red-600"}`}
                >
                  {projection.roi_com_corretor}%
                </p>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-gray-50">
                <p className="text-sm font-medium text-gray-600">
                  Investindo à SELIC
                </p>
                <p className="text-sm font-semibold text-blue-600">
                  {formatBRL(projection.valor_investimento_selic || 0)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
